import moment from 'moment'
import {v4 as uuidv4} from 'uuid';
import {jwtDecode} from 'jwt-decode'
import dayjs from 'dayjs'
import ExcelJS from 'exceljs'

function isJwtTokenFormatValid(token: unknown): token is string {
    return typeof token === 'string'
        && token !== ''
        && token !== 'undefined'
        && token !== 'null'
        && token.split('.').length === 3
}

export function getHumanDate(timestamp: number): string {
    return moment.unix(timestamp).format('DD/MM/YYYY')
}

export function getHumanDateTime(timestamp: number): string {
    return moment.unix(timestamp).format('DD/MM/YYYY, HH:mm')
}

export function getHumanDateTimeFromDateTime(dateTime: string): string {
    return moment(dateTime).format('DD/MM/YYYY, HH:mm')
}

export function getLastFromArr(array: Array<any>): string | number {
    return array[array.length - 1]
}

// Function to generate CSV with translated headers
export function generateCsv(data: Array<any>, filename: string, t: (key: string) => string, locale: string): void {
    if (data.length === 0) {
        console.error('No data to generate CSV');
        return;
    }

    // Get the headers dynamically from the data keys
    const dataKeys = Object.keys(data[0]);

    // Create headers with translations
    const headers = dataKeys.reduce((acc, key) => {
        acc[key] = t(`reportCsv.columns.${key}`) || key;
        return acc;
    }, {} as Record<string, string>);

    // Create CSV content
    const csvContent = [
        Object.values(headers).join(','), // Header row
        ...data.map(row => dataKeys.map(key => {
            if (key === 'device_timestamp') {
                return dayjs(row[key] * 1000).format('YYYY/MM/DD HH:mm:ss');
            }
            return row[key];
        }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    const randomId = uuidv4(); // Generate UUID

    // Modify filename to include random ID and locale
    const modifiedFilename = `${filename}_${randomId}_${locale}`;

    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${modifiedFilename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function secondsToHoursMinutes(seconds: number): string {
    seconds = Number(seconds)
    const hh = Math.floor(seconds / 3600).toString().padStart(2, '0')
    const mm = Math.floor((seconds / 60) % 60).toString().padStart(2, '0')

    return hh + ':' + mm
}

export function minutesToHoursMinutes(minutes: number): string {
    minutes = Number(minutes)
    const hh = Math.floor(minutes / 60).toString().padStart(2, '0')
    const mm = Math.floor(minutes % 60).toString().padStart(2, '0')

    return hh + ':' + mm
}

export function getDateTimeForChartJs(timestamp: number): string {
    // Konwertuj timestamp UTC na lokalną strefę czasową
    const utcMoment = moment.unix(timestamp);
    const localMoment = utcMoment.local();
    return localMoment.format('YYYY-MM-DD HH:mm:ss')
}

export function getHumanDateTimeWithWeekDayName(timestamp: number): string {
    return moment.unix(timestamp).format('DD/MM/YYYY, HH:mm (ddd)')
}

export function checkIfAdminOrBoss(): boolean {
    let isAdminOrBoss = false
    const token: string | null = localStorage.getItem('jwt_token')
    if (isJwtTokenFormatValid(token)) {
        try {
            const decodedToken: any = jwtDecode(token)
            const token_roles: Array<string> = decodedToken.roles
            if (token_roles.includes('ROLE_ADMIN') || token_roles.includes('ROLE_BOSS'))
                isAdminOrBoss = true
        } catch (e) {
            // Malformed/expired token: treat as not admin/boss instead of crashing.
        }
    }

    return isAdminOrBoss
}

export function datePrettier(date: Date): string {
    return date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0') + ' ' + date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0')
}

export function userDetails(): any {
    const token: string = localStorage.getItem('jwt_token') as string
    if (isJwtTokenFormatValid(token)) {
        try {
            return jwtDecode(token);
        } catch (e) {
            // Malformed/expired token: treat as logged out instead of crashing the app.
            return null;
        }
    }
    return null;
}
export function hasRole(role: string): boolean {
    return userDetails()?.roles.includes(role) ?? false;
}

export function canAccessAssetModule(roles: string[] = userDetails()?.roles ?? []): boolean {
    return roles.includes('ROLE_ASSET') || (roles.includes('ROLE_ADMIN') && roles.includes('ROLE_BOSS'));
}

/**
 * Same role bar as the /infrastructure page (router/index.ts's isInfrastructurePath, Menu.vue):
 * full superadmin, or a company's asset owner/admin. Distinct from canEditAssetGroups() below,
 * which is intentionally broader (also allows ROLE_ADMIN or ROLE_BOSS alone).
 */
export function canAccessInfrastructure(roles: string[] = userDetails()?.roles ?? []): boolean {
    const isSuperAdmin = roles.includes('ROLE_ADMIN') && roles.includes('ROLE_BOSS');
    return isSuperAdmin || (roles.includes('ROLE_ASSET') && (roles.includes('ROLE_ASSET_OWNER') || roles.includes('ROLE_ASSET_ADMIN')));
}

/**
 * Check if user has restricted ROLE_ASSET access (read-only)
 * Returns true when user has ROLE_ASSET but NOT ROLE_BOSS, ROLE_ASSET_ADMIN or ROLE_ADMIN
 * This means read-only access to asset groups
 */
export function isRestrictedAssetUser(): boolean {
    const user = userDetails();
    if (!user || !user.roles) {
        return false;
    }

    const roles: string[] = user.roles;
    const hasAsset = roles.includes('ROLE_ASSET');
    const hasBoss = roles.includes('ROLE_BOSS');
    const hasAssetAdmin = roles.includes('ROLE_ASSET_ADMIN');
    const hasAdmin = roles.includes('ROLE_ADMIN');

    // Return true only if user has ROLE_ASSET but NOT ROLE_BOSS, ROLE_ASSET_ADMIN or ROLE_ADMIN
    return hasAsset && !hasBoss && !hasAssetAdmin && !hasAdmin;
}

/**
 * Check if user can edit asset groups
 * Returns true for ROLE_ADMIN, ROLE_ASSET_OWNER, ROLE_BOSS + ROLE_ASSET or ROLE_ASSET_ADMIN + ROLE_ASSET
 */
export function canEditAssetGroups(): boolean {
    const user = userDetails();
    if (!user || !user.roles) {
        return false;
    }

    const roles: string[] = user.roles;
    const hasAsset = roles.includes('ROLE_ASSET');
    const hasBoss = roles.includes('ROLE_BOSS');
    const hasAssetAdmin = roles.includes('ROLE_ASSET_ADMIN');
    const hasAssetOwner = roles.includes('ROLE_ASSET_OWNER');
    const hasAdmin = roles.includes('ROLE_ADMIN');

    return hasAdmin || hasAssetOwner || (hasBoss && hasAsset) || (hasAssetAdmin && hasAsset);
}

/**
 * Check if user can create asset groups
 * Returns true for ROLE_ADMIN, ROLE_ASSET_OWNER, ROLE_BOSS + ROLE_ASSET or ROLE_ASSET_ADMIN + ROLE_ASSET
 */
export function canCreateAssetGroups(): boolean {
    const user = userDetails();
    if (!user || !user.roles) {
        return false;
    }

    const roles: string[] = user.roles;
    const hasAsset = roles.includes('ROLE_ASSET');
    const hasBoss = roles.includes('ROLE_BOSS');
    const hasAssetAdmin = roles.includes('ROLE_ASSET_ADMIN');
    const hasAssetOwner = roles.includes('ROLE_ASSET_OWNER');
    const hasAdmin = roles.includes('ROLE_ADMIN');

    return hasAdmin || hasAssetOwner || (hasBoss && hasAsset) || (hasAssetAdmin && hasAsset);
}

/**
 * Check if user can change asset group hierarchy (parent group)
 * Returns true for ROLE_ADMIN, ROLE_ASSET_OWNER, ROLE_BOSS + ROLE_ASSET or ROLE_ASSET_ADMIN + ROLE_ASSET
 */
export function canChangeAssetGroupHierarchy(): boolean {
    const user = userDetails();
    if (!user || !user.roles) {
        return false;
    }

    const roles: string[] = user.roles;
    const hasAsset = roles.includes('ROLE_ASSET');
    const hasBoss = roles.includes('ROLE_BOSS');
    const hasAssetAdmin = roles.includes('ROLE_ASSET_ADMIN');
    const hasAssetOwner = roles.includes('ROLE_ASSET_OWNER');
    const hasAdmin = roles.includes('ROLE_ADMIN');

    return hasAdmin || hasAssetOwner || (hasBoss && hasAsset) || (hasAssetAdmin && hasAsset);
}

// Function to generate Excel file with translated headers
export async function generateExcel(data: Array<any>, filename: string, t: (key: string) => string, locale: string): Promise<void> {
    if (data.length === 0) {
        console.error('No data to generate Excel');
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Data');

    // Get the headers dynamically from the data keys
    const dataKeys = Object.keys(data[0]);

    // Create headers with translations
    const headers = dataKeys.map(key => ({
        header: t(`reportCsv.columns.${key}`) || key,
        key: key,
        width: 20
    }));

    worksheet.columns = headers;

    // Add data rows
    data.forEach(row => {
        const rowData: any = {};
        dataKeys.forEach(key => {
            if (key === 'device_timestamp' || key === 'created_at') {
                rowData[key] = dayjs(row[key] * 1000).format('YYYY/MM/DD HH:mm:ss');
            } else {
                rowData[key] = row[key];
            }
        });
        worksheet.addRow(rowData);
    });

    // Style the header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF67F01' }
    };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

    // Generate buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');

    const randomId = uuidv4(); // Generate UUID
    const modifiedFilename = `${filename}_${randomId}_${locale}`;

    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${modifiedFilename}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
